"use client";

import { Slot } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { PanelLeft } from "lucide-react";
import Link from "next/link";
import * as React from "react";

import PatternImage from "~/../public/pattern.svg";
import { Button } from "~/components/button";
import { Input } from "~/components/input";
import { Separator } from "~/components/separator";
import { Sheet, SheetContent } from "~/components/sheet";
import { Skeleton } from "~/components/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "~/components/tooltip";
import { useIsMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

interface SidebarContext {
	state: "collapsed" | "expanded";
	open: boolean;
	setOpen: (open: boolean) => void;
	openMobile: boolean;
	setOpenMobile: (open: boolean) => void;
	isMobile: boolean;
	toggleSidebar: () => void;
}

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
	const context = React.useContext(SidebarContext);
	if (!context) {
		throw new Error("useSidebar must be used within a SidebarProvider.");
	}

	return context;
}

function SidebarProvider({ ref: reference, defaultOpen = true, open: openProperty, onOpenChange: setOpenProperty, className, style, children, ...props }: {
	defaultOpen?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
} & { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	const isMobile = useIsMobile();
	const [openMobile, setOpenMobile] = React.useState(false);

	// This is the internal state of the sidebar.
	// We use openProp and setOpenProp for control from outside the component.
	const [_open, _setOpen] = React.useState(defaultOpen);
	const open = openProperty ?? _open;
	const setOpen = React.useCallback(
		(value: boolean | ((value: boolean) => boolean)) => {
			const openState = typeof value === "function" ? value(open) : value;
			if (setOpenProperty) {
				setOpenProperty(openState);
			}
			else {
				_setOpen(openState);
			}

			// This sets the cookie to keep the sidebar state.
			document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
		},
		[setOpenProperty, open]
	);

	// Helper to toggle the sidebar.
	const toggleSidebar = React.useCallback(() => {
		return isMobile
			? setOpenMobile((open) => !open)
			: setOpen((open) => !open);
	}, [isMobile, setOpen, setOpenMobile]);

	// Adds a keyboard shortcut to toggle the sidebar.
	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === SIDEBAR_KEYBOARD_SHORTCUT
				&& (event.metaKey || event.ctrlKey)
			) {
				event.preventDefault();
				toggleSidebar();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [toggleSidebar]);

	// We add a state so that we can do data-state="expanded" or "collapsed".
	// This makes it easier to style the sidebar with Tailwind classes.
	const state = open ? "expanded" : "collapsed";

	const contextValue = React.useMemo<SidebarContext>(
		() => ({
			state,
			open,
			setOpen,
			isMobile,
			openMobile,
			setOpenMobile,
			toggleSidebar,
		}),
		[state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
	);

	return (
		<SidebarContext value={contextValue}>
			<TooltipProvider delayDuration={0}>
				<div
					className={cn(
						"group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar",
						className
					)}
					style={
						{
							"--sidebar-width": SIDEBAR_WIDTH,
							"--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
							...style,
						} as React.CSSProperties
					}
					ref={reference}
					{...props}
				>
					{children}
				</div>
			</TooltipProvider>
		</SidebarContext>
	);
}
SidebarProvider.displayName = "SidebarProvider";

function Sidebar({ ref: reference, side = "left", variant = "sidebar", collapsible = "offcanvas", className, children, ...props }: { ref?: React.Ref<HTMLDivElement> } & {
	side?: "left" | "right";
	variant?: "floating" | "inset" | "sidebar";
	collapsible?: "icon" | "none" | "offcanvas";
} & React.ComponentProps<"div">) {
	const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

	if (collapsible === "none") {
		return (
			<div
				className={cn(
					"flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
					className
				)}
				ref={reference}
				{...props}
			>
				{children}
			</div>
		);
	}

	if (isMobile) {
		return (
			<Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
				<SheetContent
					style={
						{
							"--sidebar-width": SIDEBAR_WIDTH_MOBILE,
						} as React.CSSProperties
					}
					className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
					data-mobile="true"
					data-sidebar="sidebar"
					side={side}
				>
					<div className="flex size-full flex-col">{children}</div>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<div
			className="group peer hidden text-sidebar-foreground md:block"
			data-collapsible={state === "collapsed" ? collapsible : ""}
			data-side={side}
			data-state={state}
			data-variant={variant}
			ref={reference}
		>
			{/* This is what handles the sidebar gap on desktop */}
			<div
				className={cn(
					"duration-200 relative h-svh w-[--sidebar-width] bg-transparent transition-[width] ease-linear",
					"group-data-[collapsible=offcanvas]:w-0",
					"group-data-[side=right]:rotate-180",
					variant === "floating" || variant === "inset"
						? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
						: "group-data-[collapsible=icon]:w-[--sidebar-width-icon]"
				)}
			/>
			<div
				className={cn(
					"duration-200 fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex",
					side === "left"
						? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
						: "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
					// Adjust the padding for floating and inset variants.
					variant === "floating" || variant === "inset"
						? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
						: "group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l",
					className
				)}
				{...props}
			>
				<div
					className="flex size-full flex-col bg-sidebar pl-1 group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
					data-sidebar="sidebar"
				>
					<div className="absolute inset-y-0 left-0 h-full w-1 bg-gradient-to-b from-purple-500 to-purple-600" />
					{children}
				</div>
			</div>
		</div>
	);
}
Sidebar.displayName = "Sidebar";

function SidebarTrigger({ ref: reference, className, onClick, ...props }: { ref?: React.Ref<React.ElementRef<typeof Button>> } & React.ComponentProps<typeof Button>) {
	const { toggleSidebar } = useSidebar();

	return (
		<Button
			className={cn("h-7 w-7", className)}
			data-sidebar="trigger"
			ref={reference}
			size="icon"
			variant="ghost"
			onClick={(event) => {
				onClick?.(event);
				toggleSidebar();
			}}
			{...props}
		>
			<PanelLeft />
			<span className="sr-only">Toggle Sidebar</span>
		</Button>
	);
}
SidebarTrigger.displayName = "SidebarTrigger";

function SidebarRail({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<"button">) {
	const { toggleSidebar } = useSidebar();

	return (
		<button
			className={cn(
				"absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
				"[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
				"[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
				"group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
				"[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
				"[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
				className
			)}
			aria-label="Toggle Sidebar"
			data-sidebar="rail"
			ref={reference}
			tabIndex={-1}
			title="Toggle Sidebar"
			type="button"
			onClick={toggleSidebar}
			{...props}
		/>
	);
}
SidebarRail.displayName = "SidebarRail";

function SidebarInset({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"main">) {
	return (
		<main
			className={cn(
				"relative flex min-h-svh flex-1 flex-col bg-background border",
				"peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
				className
			)}
			style={{
				backgroundImage: `url(${PatternImage.src})`,
				backgroundSize: "32rem"
			}}
			ref={reference}
			{...props}
		/>
	);
}
SidebarInset.displayName = "SidebarInset";

function SidebarInput({ ref: reference, className, ...props }: { ref?: React.Ref<React.ElementRef<typeof Input>> } & React.ComponentProps<typeof Input>) {
	return (
		<Input
			className={cn(
				"h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
				className
			)}
			data-sidebar="input"
			ref={reference}
			{...props}
		/>
	);
}
SidebarInput.displayName = "SidebarInput";

function SidebarHeader({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn("flex flex-col gap-2 p-2", className)}
			data-sidebar="header"
			ref={reference}
			{...props}
		/>
	);
}
SidebarHeader.displayName = "SidebarHeader";

function SidebarFooter({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn("flex flex-col gap-2 p-2", className)}
			data-sidebar="footer"
			ref={reference}
			{...props}
		/>
	);
}
SidebarFooter.displayName = "SidebarFooter";

function SidebarSeparator({ ref: reference, className, ...props }: { ref?: React.Ref<React.ElementRef<typeof Separator>> } & React.ComponentProps<typeof Separator>) {
	return (
		<Separator
			className={cn("mx-2 w-auto bg-sidebar-border", className)}
			data-sidebar="separator"
			ref={reference}
			{...props}
		/>
	);
}
SidebarSeparator.displayName = "SidebarSeparator";

function SidebarContent({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn(
				"flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
				className
			)}
			data-sidebar="content"
			ref={reference}
			{...props}
		/>
	);
}
SidebarContent.displayName = "SidebarContent";

function SidebarGroup({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
			data-sidebar="group"
			ref={reference}
			{...props}
		/>
	);
}
SidebarGroup.displayName = "SidebarGroup";

function SidebarGroupLabel({ ref: reference, className, asChild = false, ...props }: { asChild?: boolean } & { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	const Comp = asChild ? Slot : "div";

	return (
		<Comp
			className={cn(
				"duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
				"group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
				className
			)}
			data-sidebar="group-label"
			ref={reference}
			{...props}
		/>
	);
}
SidebarGroupLabel.displayName = "SidebarGroupLabel";

function SidebarGroupAction({ ref: reference, className, asChild = false, ...props }: { asChild?: boolean } & { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<"button">) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			className={cn(
				"absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
				// Increases the hit area of the button on mobile.
				"after:absolute after:-inset-2 after:md:hidden",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			data-sidebar="group-action"
			ref={reference}
			{...props}
		/>
	);
}
SidebarGroupAction.displayName = "SidebarGroupAction";

function SidebarGroupContent({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn("w-full text-sm", className)}
			data-sidebar="group-content"
			ref={reference}
			{...props}
		/>
	);
}
SidebarGroupContent.displayName = "SidebarGroupContent";

function SidebarMenu({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLUListElement> } & React.ComponentProps<"ul">) {
	return (
		<ul
			className={cn("flex w-full min-w-0 flex-col gap-1", className)}
			data-sidebar="menu"
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenu.displayName = "SidebarMenu";

function SidebarMenuItem({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLLIElement> } & React.ComponentProps<"li">) {
	return (
		<li
			className={cn("group/menu-item relative", className)}
			data-sidebar="menu-item"
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
	"peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
				outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
			},
			size: {
				default: "h-8 text-sm",
				sm: "h-7 text-xs",
				lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

function SidebarMenuButton({ ref: reference, asChild = false, isActive = false, variant = "default", size = "default", tooltip, className, ...props }: {
	asChild?: boolean;
	isActive?: boolean;
	tooltip?: React.ComponentProps<typeof TooltipContent> | string;
} & { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<"button"> & VariantProps<typeof sidebarMenuButtonVariants>) {
	const Comp = asChild ? Slot : "button";
	const { isMobile, state } = useSidebar();

	const button = (
		<Comp
			className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
			data-active={isActive}
			data-sidebar="menu-button"
			data-size={size}
			ref={reference}
			{...props}
		/>
	);

	if (!tooltip) {
		return button;
	}

	if (typeof tooltip === "string") {
		tooltip = {
			children: tooltip,
		};
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent
				align="center"
				hidden={state !== "collapsed" || isMobile}
				side="right"
				{...tooltip}
			/>
		</Tooltip>
	);
}
SidebarMenuButton.displayName = "SidebarMenuButton";

function SidebarMenuAction({ ref: reference, className, asChild = false, showOnHover = false, ...props }: {
	asChild?: boolean;
	showOnHover?: boolean;
} & { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<"button">) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			className={cn(
				"absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0",
				// Increases the hit area of the button on mobile.
				"after:absolute after:-inset-2 after:md:hidden",
				"peer-data-[size=sm]/menu-button:top-1",
				"peer-data-[size=default]/menu-button:top-1.5",
				"peer-data-[size=lg]/menu-button:top-2.5",
				"group-data-[collapsible=icon]:hidden",
				showOnHover
				&& "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
				className
			)}
			data-sidebar="menu-action"
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenuAction.displayName = "SidebarMenuAction";

function SidebarMenuBadge({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<"div">) {
	return (
		<div
			className={cn(
				"absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
				"peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
				"peer-data-[size=sm]/menu-button:top-1",
				"peer-data-[size=default]/menu-button:top-1.5",
				"peer-data-[size=lg]/menu-button:top-2.5",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			data-sidebar="menu-badge"
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenuBadge.displayName = "SidebarMenuBadge";

function SidebarMenuSkeleton({ ref: reference, className, showIcon = false, ...props }: { ref?: React.Ref<HTMLDivElement> } & {
	showIcon?: boolean;
} & React.ComponentProps<"div">) {
	// Random width between 50 to 90%.
	const width = React.useMemo(() => {
		return `${Math.floor(Math.random() * 40) + 50}%`;
	}, []);

	return (
		<div
			className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
			data-sidebar="menu-skeleton"
			ref={reference}
			{...props}
		>
			{showIcon && (
				<Skeleton
					className="size-4 rounded-md"
					data-sidebar="menu-skeleton-icon"
				/>
			)}
			<Skeleton
				style={
					{
						"--skeleton-width": width,
					} as React.CSSProperties
				}
				className="h-4 max-w-[--skeleton-width] flex-1"
				data-sidebar="menu-skeleton-text"
			/>
		</div>
	);
}
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

function SidebarMenuSub({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLUListElement> } & React.ComponentProps<"ul">) {
	return (
		<ul
			className={cn(
				"mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			data-sidebar="menu-sub"
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = ({ ref: reference, ...props }: { ref?: React.Ref<HTMLLIElement> } & React.ComponentProps<"li">) => <li ref={reference} {...props} />;
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

function SidebarMenuSubButton({ ref: reference, href, asChild = false, size = "md", isActive, className, ...props }: {
	asChild?: boolean;
	size?: "md" | "sm";
	isActive?: boolean;
} & { ref?: React.Ref<HTMLAnchorElement> } & React.ComponentProps<"a">) {
	const Comp = asChild ? Slot : Link;

	return (
		<Comp
			className={cn(
				"flex h-7 min-w-0 -translate-x-px items-center gap-2 rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
				"data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
				size === "sm" && "text-xs",
				size === "md" && "text-sm",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			data-active={isActive}
			data-sidebar="menu-sub-button"
			data-size={size}
			href={href || "#"}
			ref={reference}
			{...props}
		/>
	);
}
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInput,
	SidebarInset,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarSeparator,
	SidebarTrigger,
	// eslint-disable-next-line react-refresh/only-export-components
	useSidebar,
};
